import React from 'react'
import { Divider, Modal, Input, Empty } from 'antd'
import ReactPlayer from 'react-player'
import { Spotify } from 'react-spotify-embed'
import { CloseOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { Done } from '@mui/icons-material'
import { PlusOutlined } from '@ant-design/icons'
import { useAtom } from 'jotai'
import { useAuth0 } from '@auth0/auth0-react'
import userClient from '../../../Api/Lirari/Clients/userClient'
import { useParams } from 'react-router-dom'
import { myUserAtom } from '../../../State/Musician/myUserAtom'
import { NewPortfolioLink, Provider, PortfolioLink } from '../../../Api/Lirari/Types/portfolioLinks'

const MusicianPortfolioPopup = ({
  popupOpen,
  setPopupOpen,
  editMode,
}: {
  popupOpen: boolean
  setPopupOpen: (value: boolean) => void
  editMode: boolean
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [target, setTarget] = React.useState<'video' | 'audio' | null>(null)
  const [videoToAdd, setVideoToAdd] = React.useState('')
  const [audioToAdd, setAudioToAdd] = React.useState('')
  const [myUser, setMyUser] = useAtom(myUserAtom)
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const { uid: userId } = useParams()
  const [error, setError] = React.useState<string | null>(null)

  const isMobile = window.innerWidth <= 768

  const videos = myUser.portfolioLinks.filter((link) => link.provider === 'you_tube')
  const audios = myUser.portfolioLinks.filter((link) => ['spotify', 'sound_cloud'].includes(link.provider))

  const handleRemoveLink = async (selectedLink: PortfolioLink) => {
    try {
      if (isAuthenticated && userId === myUser.id) {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            scope: 'user:all',
          },
        })
        await userClient.removePortfolioLink(userId, selectedLink.id, token)
        setMyUser({
          ...myUser,
          portfolioLinks: myUser.portfolioLinks.filter((link) => link.id !== selectedLink.id),
        })
      }
      setError(null)
    } catch (err) {
      console.error('Error removing link:', err)
      setError('Failed to remove link')
    }
  }

  const getProvider = (url: string): Provider => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'you_tube'
    if (url.includes('spotify.com')) return 'spotify'
    if (url.includes('soundcloud.com')) return 'sound_cloud'
    return 'you_tube' // default to youtube if unknown
  }

  const handleSubmit = async () => {
    try {
      const url = target === 'video' ? videoToAdd : audioToAdd
      if (url && !myUser.portfolioLinks.some((link) => link.url === url)) {
        const newLink: NewPortfolioLink = {
          url,
          provider: getProvider(url),
        }

        if (isAuthenticated && userId === myUser.id) {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              scope: 'user:all',
            },
          })
          const response = await userClient.updatePortfolioLinks(userId, newLink, token)
          setMyUser({
            ...myUser,
            portfolioLinks: [...myUser.portfolioLinks, response],
          })
        }
        setError(null)
      }
    } catch (err) {
      console.error('Error adding media:', err)
      setError('Failed to add media')
    }

    setIsModalOpen(false)
  }

  const EditPortfolioModal = (
    <Modal
      centered
      open={isModalOpen}
      onCancel={() => {
        setIsModalOpen(false)
      }}
      footer={[]}
    >
      <div className="flex flex-col items-center">
        <Input
          variant="borderless"
          className="text-white bg-transparent border-b-white hover:border-b-white focus:border-b-white text-lg text-center hover:bg-transparent focus:bg-transparent placeholder:text-white"
          placeholder={target ? target : ''}
          onChange={(e) => {
            if (target === 'video') {
              setVideoToAdd(e.target.value)
            } else if (target === 'audio') {
              setAudioToAdd(e.target.value)
            }
          }}
        />
        <button
          className="flex items-center justify-center mt-2.5 h-[50px] w-[50px] rounded-full bg-[#d9d9d9]"
          onClick={handleSubmit}
        >
          <Done />
        </button>
      </div>
    </Modal>
  )

  return (
    <Modal
      className="overflow-y-auto h-[90%] bg-black"
      width={850}
      centered
      open={popupOpen}
      onCancel={() => setPopupOpen(false)}
      footer={[]}
      closeIcon={<CloseOutlined className="text-white" />}
    >
      <p className="flex text-center font-bold text-2xl mt-0 justify-center">Portfolio</p>
      {error && <div className="text-center text-red-500 mb-4">{error}</div>}
      <div>
        <p className="text-lg mb-0 mt-0 lg:text-xl">Videos</p>
        <Divider />
        <div className="flex flex-col items-center">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div className="flex space-x-2.5 mb-[50px] text-white w-full lg:w-1/2 h-full" key={video.id}>
                <ReactPlayer
                  controls={true}
                  height={isMobile ? '100%' : undefined}
                  style={{ width: '100%', height: '100%' }}
                  url={video.url}
                />
                {editMode ? (
                  <MinusCircleOutlined
                    className="self-center w-10 h-10 [&>svg]:w-10 [&>svg]:h-10"
                    onClick={() => handleRemoveLink(video)}
                  />
                ) : null}
              </div>
            ))
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-white">
                  {editMode ? 'Click the button below to add videos' : 'No videos available'}
                </span>
              }
            />
          )}
        </div>

        {editMode ? (
          <button
            className="flex m-auto w-full h-[180px] lg:w-1/2 cursor-pointer justify-center items-center lg:h-[300px] opacity-50 rounded-2xl bg-white"
            onClick={() => {
              setIsModalOpen(true)
              setTarget('video')
            }}
          >
            <PlusOutlined className="[&>svg]:w-12 [&>svg]:h-12" />
          </button>
        ) : null}

        <p className="text-lg mb-0 mt-0 lg:text-xl">Audios</p>

        <Divider />

        <div className="flex flex-col lg:grid grid-cols-2 gap-12 justify-center">
          {audios.length > 0 ? (
            audios.map((audio) => (
              <div key={audio.id} className="flex text-white gap-2.5">
                {audio.provider === 'spotify' ? (
                  <Spotify link={audio.url} height={155} />
                ) : (
                  <ReactPlayer width={300} height={155} url={audio.url} />
                )}
                {editMode ? (
                  <MinusCircleOutlined
                    className="self-center w-8 h-8 [&>svg]:w-8 [&>svg]:h-8"
                    onClick={() => handleRemoveLink(audio)}
                  />
                ) : null}
              </div>
            ))
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-white">
                  {editMode ? 'Click the button below to add audio' : 'No audio available'}
                </span>
              }
            />
          )}
          {editMode ? (
            <button
              className="w-[300px] h-[155px] opacity-50 rounded-2xl bg-white"
              onClick={() => {
                setIsModalOpen(true)
                setTarget('audio')
              }}
            >
              <PlusOutlined className="[&>svg]:w-6 [&>svg]:h-6" />
            </button>
          ) : null}
        </div>
      </div>
      {isModalOpen ? EditPortfolioModal : null}
    </Modal>
  )
}

export default MusicianPortfolioPopup
